import {
  GUIDE_EMBED_IDENTIFY_CLEAR_MESSAGE_TYPE,
  GUIDE_EMBED_IDENTIFY_MESSAGE_TYPE,
  GUIDE_EMBED_ORIGIN,
  GUIDE_EMBED_READY_MESSAGE_TYPE,
  GUIDE_READY_SOURCES,
} from './config';
import { USER_CHANGED_EVENT } from '~src/user/events';

const MAX_VALUE_LENGTH = 160;
const MAX_PROPERTIES = 24;
const identifiedProfiles = new WeakMap();

const sanitizeValue = (value) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }

  if (typeof value === 'string') {
    return value.slice(0, MAX_VALUE_LENGTH);
  }

  return undefined;
};

export const sanitizeGuideProperties = (properties = {}) =>
  Object.entries(properties).reduce((sanitized, [key, value]) => {
    if (Object.keys(sanitized).length >= MAX_PROPERTIES) return sanitized;

    const sanitizedValue = sanitizeValue(value);
    if (sanitizedValue !== undefined) {
      sanitized[key] = sanitizedValue;
    }
    return sanitized;
  }, {});

const sanitizeIdentityField = (value) => sanitizeValue(value);

export const buildGuideIdentifyPayload = (
  startupResponse,
  { topicSlug, pathname } = {}
) => {
  const user = startupResponse && startupResponse.user;
  const profileId = sanitizeIdentityField(user && user.objectId);
  if (profileId === undefined) return null;

  const org = user.org_pointer || {};
  const orgId = sanitizeIdentityField(org.objectId);
  const payload = {
    type: GUIDE_EMBED_IDENTIFY_MESSAGE_TYPE,
    profileId,
    properties: sanitizeGuideProperties({
      org_id: orgId,
      org_nome: org.nome,
      ruolo: user.ruolo,
      contesto_modulo: topicSlug,
      contesto_rotta: pathname,
      app: 'giotto',
    }),
  };

  [
    ['firstName', user.first_name],
    ['lastName', user.last_name],
    ['email', user.email || user.username],
  ].forEach(([key, value]) => {
    const sanitizedValue = sanitizeIdentityField(value);
    if (sanitizedValue !== undefined) payload[key] = sanitizedValue;
  });

  if (orgId !== undefined) {
    payload.group = {
      id: orgId,
      properties: sanitizeGuideProperties({
        piano: org.plan || org.tipo_licenza,
      }),
    };
    const orgName = sanitizeIdentityField(org.nome);
    if (orgName !== undefined) payload.group.nome = orgName;
  }

  return payload;
};

export const readHelpReadySource = (event) => {
  const data = event && event.data;

  if (
    !event ||
    event.origin !== GUIDE_EMBED_ORIGIN ||
    !data ||
    typeof data !== 'object' ||
    Array.isArray(data) ||
    data.type !== GUIDE_EMBED_READY_MESSAGE_TYPE ||
    GUIDE_READY_SOURCES.indexOf(data.source) === -1
  ) {
    return null;
  }

  return data.source;
};

export const clearGuideIdentity = (frameWindow) => {
  if (!frameWindow || typeof frameWindow.postMessage !== 'function') return;

  frameWindow.postMessage(
    { type: GUIDE_EMBED_IDENTIFY_CLEAR_MESSAGE_TYPE },
    GUIDE_EMBED_ORIGIN
  );
  identifiedProfiles.delete(frameWindow);
};

export const clearHelpEmbedIdentities = (root = document) => {
  if (!root || typeof root.querySelectorAll !== 'function') return;

  root.querySelectorAll('iframe').forEach((iframe) => {
    const src = iframe.getAttribute('src') || '';
    let origin;
    try {
      origin = new URL(src, window.location.href).origin;
    } catch (error) {
      return;
    }
    if (origin !== GUIDE_EMBED_ORIGIN) return;

    clearGuideIdentity(iframe.contentWindow);
  });
};

export const identifyGuideFrame = (frameWindow, startupResponse, context) => {
  if (!frameWindow || typeof frameWindow.postMessage !== 'function') {
    return false;
  }

  const payload = buildGuideIdentifyPayload(startupResponse, context);
  if (!payload) return false;

  const previousProfileId = identifiedProfiles.get(frameWindow);
  if (
    previousProfileId !== undefined &&
    previousProfileId !== payload.profileId
  ) {
    clearGuideIdentity(frameWindow);
  }

  frameWindow.postMessage(payload, GUIDE_EMBED_ORIGIN);
  identifiedProfiles.set(frameWindow, payload.profileId);
  return true;
};

export const bindHelpIdentifyListener = (getStartupResponse, getContext) => {
  if (window.__giottoHelpIdentifyListenerBound) return;

  window.__giottoHelpIdentifyListenerBound = true;
  window.addEventListener('message', async (event) => {
    const source = readHelpReadySource(event);
    if (!source) return;

    try {
      const startupResponse = await getStartupResponse();
      identifyGuideFrame(event.source, startupResponse, getContext(source));
    } catch (error) {
      // Senza utente l'embed inviera' autonomamente una screen view anonima.
    }
  });

  window.addEventListener(USER_CHANGED_EVENT, () => {
    clearHelpEmbedIdentities();
  });
};
